import { useNavigate } from "react-router-dom";
import { ProductListSkeleton } from "./LoadingSkeleton";
import { supabase } from "@/lib/supabase";
import { useProductQuery } from "@/hooks/useOptimizedQuery";
import { cacheKeys } from "@/lib/cache";

const FeaturedProducts = () => {
  const navigate = useNavigate();

  // Use optimized product query with background refresh
  const { data: products, isLoading, cacheHit } = useProductQuery(
    cacheKeys.featuredProducts(),
    async () => {
      console.log('🔄 Fetching featured products from database...');
      const { data } = await supabase
        .from("products")
        .select("*, shops(*), categories(*)")
        .eq("status", "active")
        .limit(8);
      return data ?? [];
    },
    {
      backgroundRefresh: true, // Refresh featured products in background
      optimisticKey: "featured_products",
    }
  );

  // Show cache performance in development
  if (import.meta.env.DEV && cacheHit) {
    console.log('⚡ Featured products loaded from cache');
  }

  if (isLoading) {
    return (
      <section className="space-y-8">
        <div className="text-center space-y-3">
          <div className="h-8 bg-muted rounded w-48 mx-auto animate-pulse"></div>
          <div className="h-6 bg-muted rounded w-80 mx-auto animate-pulse"></div>
        </div>
        <ProductListSkeleton />
      </section>
    );
  }

  if (!products?.length) {
    return (
      <section className="space-y-8">
        <div className="text-center space-y-3">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            Featured Products
          </h2>
          <p className="text-muted-foreground text-lg">
            No featured products available at the moment.
          </p>
        </div>
      </section>
    );
  }
  return (
    <section className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
          Featured Products
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Discover our handpicked selection of premium Bahamian products
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products.map((product: any) => (
          <div
            key={product.id}
            className="group bg-card border border-border/40 rounded-lg overflow-hidden hover:shadow-luxury-md transition-all duration-300 cursor-pointer hover:border-border"
            onClick={() => navigate(`/products/${product.id}`)}
          >
            <div className="aspect-square overflow-hidden bg-muted">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <span className="text-muted-foreground text-sm">No image</span>
                </div>
              )}
            </div>
            <div className="p-6 space-y-3">
              <div>
                <h3 className="font-semibold text-foreground group-hover:text-foreground/80 transition-colors line-clamp-2">
                  {product.name}
                </h3>
                {product.shops && (
                  <p className="text-sm text-muted-foreground mt-1">
                    by {product.shops.name}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-foreground">
                  BSD {(product.price_cents / 100).toFixed(2)}
                </span>
                {product.categories && (
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                    {product.categories.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeaturedProducts;